import {
  DynamicFontChangeHandler,
  DynamicFontVariants,
  useJourneyDynamicFontStore,
} from 'src/store/journey-dynamic-font';
import { NeueListbox } from 'src/common/form/NeueListbox';
import React, { useMemo } from 'react';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { RefreshLightIcon } from 'src/monet/icons';
import { ControlPanelActionButton } from '../../control-panel/action-button';

interface Props {
  onChange: DynamicFontChangeHandler;
  onReset: () => void;
}

export const DEFAULT_DYNAMIC_FONT_VARIANTS = ['light', 'regular', 'bold'];

export const ThemeDynamicFontsContent = ({ onChange, onReset }: Props) => {
  const isDynamicFontsReady = useJourneyDynamicFontStore((state) => state.isReady);
  const isCustomFontStrictlyUsed = useJourneyDynamicFontStore((state) => state.isCustomFontStrictlyUsed);
  const dynamicFontState = useJourneyDynamicFontStore((state) => state.state);
  const currentDynamicFont = useJourneyDynamicFontStore((state) => state.current);

  const isHeadingFontFamilyDefault = currentDynamicFont.headings.fontFamily === 'CircularXX';
  const isParagraphFontFamilyDefault = currentDynamicFont.paragraphs.fontFamily === 'CircularXX';

  const { fontFamily: currentParagraphDynamicFontFamily, variant: currentParagraphFontVariant } =
    currentDynamicFont.paragraphs;
  const { fontFamily: currentHeadingDynamicFontFamily, variant: currentHeadingFontVariant } =
    currentDynamicFont.headings;

  const headingFontOptions = useJourneyDynamicFontStore((state) => state.options.headings);
  const paragraphFontOptions = useJourneyDynamicFontStore((state) => state.options.paragraphs);

  const headingFontFamilies = useMemo(
    () => headingFontOptions.map((option) => option.fontFamily),
    [headingFontOptions]
  );

  const paragraphFontFamilies = useMemo(() => {
    return paragraphFontOptions.map((option) => option.fontFamily);
  }, [paragraphFontOptions]);

  const currentHeadingFont = useMemo(
    () => headingFontOptions.find((option) => option.fontFamily === currentHeadingDynamicFontFamily),
    [currentHeadingDynamicFontFamily, headingFontOptions]
  );

  const currentParagraphFont = paragraphFontOptions.find(
    (option) => option.fontFamily === currentParagraphDynamicFontFamily
  );

  const headingFontVariants = useMemo(() => {
    if (!isDynamicFontsReady || !currentHeadingFont) {
      return [];
    }

    if (isCustomFontStrictlyUsed) {
      return ['Custom Font'];
    }

    const foundVariants = Object.keys(currentHeadingFont.variants).map((variant) => variant.toLowerCase());
    return foundVariants;
  }, [isDynamicFontsReady, currentHeadingFont]);

  const paragraphFontVariants = useMemo(() => {
    if (!isDynamicFontsReady || !currentParagraphFont) {
      return [];
    }

    return Object.keys(currentParagraphFont.variants).map((variant) => variant.toLowerCase());
  }, [currentParagraphFont, isDynamicFontsReady]);

  const getFontFamilyChangeHandler = (type: 'headings' | 'paragraphs') => (selected: string) => {
    if (isDynamicFontsReady) {
      const currentFont = useJourneyDynamicFontStore
        .getState()
        .options[type].find((option) => option.fontFamily === selected);

      if (currentFont) {
        onChange(type, {
          fontFamily: currentFont.fontFamily,
          variant: 'regular',
          url: currentFont.variants.regular,
        });
      }
    }
  };

  const getFontVariantChangeHandler = (type: 'headings' | 'paragraphs') => (selected: DynamicFontVariants) => {
    let toSearch = type === 'headings' ? currentHeadingDynamicFontFamily : currentParagraphDynamicFontFamily;
    if (isDynamicFontsReady) {
      const currentFont = useJourneyDynamicFontStore
        .getState()
        .options[type].find((option) => option.fontFamily === toSearch);

      if (currentFont && currentFont.variants[selected]) {
        onChange(type, {
          fontFamily: toSearch,
          variant: selected as DynamicFontVariants,
          url: currentFont.variants[selected],
        });
      }
    }
  };

  const renderHeadingSettings = () => {
    return (
      <div className='flex flex-col gap-2'>
        <span className='text-neue-journey-medium-strong'>Headings</span>
        <div className='flex gap-2'>
          <NeueListbox
            key={currentHeadingDynamicFontFamily}
            containerClassName='w-[calc(100%-6rem)]'
            buttonClassName='!px-2 py-[7px]'
            labelClassName='!pr-0 text-neue-journey-medium'
            disabled={isCustomFontStrictlyUsed}
            selected={currentHeadingDynamicFontFamily}
            options={headingFontFamilies}
            renderValue={(o: string) => <div>{o}</div>}
            renderKey={(o: string) => o}
            hideChevron
            enableSearch
            preventChangeEventOnMount
            onChange={getFontFamilyChangeHandler('headings')}
          />
          <NeueListbox
            key={currentHeadingFontVariant}
            containerClassName='w-24'
            buttonClassName='!px-2 py-[7px]'
            labelClassName='!pr-0 text-neue-journey-medium'
            disabled={isHeadingFontFamilyDefault || isCustomFontStrictlyUsed}
            selected={currentHeadingFontVariant}
            options={headingFontVariants}
            renderValue={(o: string) => o}
            preventChangeEventOnMount
            hideChevron
            renderKey={(o: string) => o.toLocaleUpperCase()}
            onChange={getFontVariantChangeHandler('headings')}
          />
        </div>
      </div>
    );
  };

  const renderParagraphSettings = () => {
    return (
      <div className='flex flex-col gap-2'>
        <span className='text-neue-journey-medium-strong'>Paragraphs</span>
        <div className='flex gap-2'>
          <NeueListbox
            containerClassName='w-[calc(100%-6rem)]'
            buttonClassName='!px-2 py-[7px]'
            disabled={isCustomFontStrictlyUsed}
            labelClassName='!pr-0 text-neue-journey-medium'
            selected={currentParagraphDynamicFontFamily}
            options={paragraphFontFamilies}
            renderValue={(o: string) => <div>{o}</div>}
            renderKey={(o: string) => o}
            hideChevron
            enableSearch
            onChange={getFontFamilyChangeHandler('paragraphs')}
          />
          <NeueListbox
            containerClassName='w-24'
            buttonClassName='!px-2 py-[7px]'
            labelClassName='!pr-0 text-neue-journey-medium'
            disabled={isParagraphFontFamilyDefault || isCustomFontStrictlyUsed}
            selected={isParagraphFontFamilyDefault ? 'regular' : currentParagraphFontVariant}
            options={paragraphFontVariants}
            renderValue={(o: string) => o}
            hideChevron
            enableSearch
            renderKey={(o: string) => <span className='capitalize'>{o}</span>}
            onChange={getFontVariantChangeHandler('paragraphs')}
          />
        </div>
      </div>
    );
  };

  const renderFontSettings = () => {
    if (isCustomFontStrictlyUsed) {
      return (
        <span className='text-neue-journey-small'>Custom fonts are strictly applied. Font settings are disabled.</span>
      );
    }
    if (dynamicFontState === 'ERROR') {
      return <span className='text-neue-journey-small text-red-500'>Error loading dynamic fonts</span>;
    }
    if (!isDynamicFontsReady) {
      return <LoadingSpinner />;
    }
    return (
      <>
        {renderHeadingSettings()} {renderParagraphSettings()}
        <ControlPanelActionButton
          label='Reset fonts'
          disabled={isHeadingFontFamilyDefault && isParagraphFontFamilyDefault}
          iconComponent={RefreshLightIcon}
          onClick={onReset}
        />
      </>
    );
  };

  return <div className='flex flex-col gap-6 text-neue-journey-fg mt-2 relative'>{renderFontSettings()}</div>;
};
